import SelectField from "../../../components/core/basic/SelectField";
import Loading from "../../../components/core/blocks/Loading";
import { useFetch } from "../../../hooks/api";
import { useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import api from "../../../utils/api";
import { successToast, errorToast } from "../../../utils/toasts";
import { Button } from "../../../components/core/basic/Button";
import { useSimpleCan } from "../../../config/casl/casl";
import { useModuleEnabled } from "../../../redux/selectors/modules";

const ControlForm = ({ defaultLineItem, onSubmit, programs, readOnly = false }) => {
  const { t } = useTranslation(["dsp"]);
  const { handleSubmit, control } = useForm({
    shouldUnregister: false,
    defaultValues: defaultLineItem,
  });
  const programOptions = useMemo(() => {
    const options =
      programs?.map((program) => ({
        label: program.name,
        value: program.id,
      })) ?? [];
    options.unshift({ label: t(($) => $.dsp["No Bidding Program"]), value: -1 });
    return options;
  }, [programs, t]);
  return (
    <Col xs={12} lg={6} className="fs-stantard">
      <Row>
        <Col xs={12}>
          <h5 className="fw-bold pb-2 pt-1">{t(($) => $.dsp["Line Item Bidding Program"])}</h5>
          <Controller
            name="program"
            control={control}
            render={({ field }) => (
              <div className="flex-fill pe-3">
                <SelectField {...field} value={field.value || -1} options={programOptions} isDisabled={readOnly} />
              </div>
            )}
          />
        </Col>
        <Col xs={12}>
          <p className="fs-5 mt-3">
            {t(($) => $.dsp["Use Line Item Programs to control the bids for this line item based on performance."])}
            <br />
            <Trans
              t={t}
              i18nKey={($) =>
                $.dsp["Click here to create a new <lineItemProgramLink>Line Item Program</lineItemProgramLink>."]
              }
              components={{ lineItemProgramLink: <Link to="/user/advertising/settings" /> }}
            />
          </p>
        </Col>
        <Col xs={12} className="d-flex justify-content-end mt-3">
          <Button
            variant="primary"
            onPress={() => {
              handleSubmit(onSubmit)();
            }}
            isDisabled={readOnly}
          >
            {t(($) => $.dsp["Save"])}
          </Button>
        </Col>
      </Row>
    </Col>
  );
};

const DSPLineItemAutomation = ({ lineItemId }) => {
  const { t } = useTranslation(["dsp"]);
  const userCanUpdateDSPEntity = useSimpleCan("update", "DSPEntity");
  const hasMultiOrgRulesModuleEnabled = useModuleEnabled("MULTI_ORG_RULES");

  const { data: lineItemControlData, loading: isLineItemControlDataLoading } = useFetch(
    ["lineItemControl", lineItemId],
    `/dsp/lineitemcontrol/?line_item=${lineItemId}`,
    {},
    {
      select: (data) => {
        if (data.data?.results?.length === 0) {
          return {
            line_item: lineItemId,
            program: null,
          };
        }
        return data.data?.results?.[0];
      },
      enabled: !!lineItemId,
    },
  );

  const { data: programsData, loading: isProgramsDataLoading } = useFetch(
    ["programs", "lineitembidding"],
    "/programs/?engine=lineitem_bidding",
    { all_user_orgs: hasMultiOrgRulesModuleEnabled },
    {
      select: (data) => data?.data?.results ?? [],
    },
  );

  const queryClient = useQueryClient();

  const createControl = useMutation({
    mutationFn: async (data) => {
      if (data.program === -1) {
        data.program = null;
      }
      await api.post("/dsp/lineitemcontrol/", data);
    },

    onMutate: () => {},

    onSuccess: () => {
      successToast({
        message: t(($) => $.dsp["Updated Line Item"]),
      });
      queryClient.invalidateQueries({
        queryKey: ["lineItemControl"],
      });
    },

    onError: (error) => {
      errorToast({
        message: t(($) => $.dsp["Failed to create Line Item"]),
        error,
      });
    },
  });

  const updateControl = useMutation({
    mutationFn: async (data) => {
      if (data.program === -1) {
        data.program = null;
      }
      await api.patch(`/dsp/lineitemcontrol/${lineItemControlData.id}/`, data);
    },

    onMutate: () => {},

    onSuccess: () => {
      successToast({
        message: t(($) => $.dsp["Updated Line Item"]),
      });
      queryClient.invalidateQueries({
        queryKey: ["lineItemControl"],
      });
    },

    onError: (error) => {
      errorToast({
        message: t(($) => $.dsp["Failed to update Line Item"]),
        error,
      });
    },
  });

  return isLineItemControlDataLoading || isProgramsDataLoading ?
      <Loading />
    : lineItemControlData && (
        <ControlForm
          programs={programsData}
          defaultLineItem={lineItemControlData}
          onSubmit={lineItemControlData?.id ? updateControl.mutate : createControl.mutate}
          readOnly={!userCanUpdateDSPEntity}
        />
      );
};

export default DSPLineItemAutomation;
